"use client";

// =========================================================================
// Form + data wiring for the grant-contact-access modal.
//
// USER_ROLES_AND_ACCESS: the grant is a row in the per-contact access table
// keyed by (app_user, contact) with an access level RLS reads. Nothing here
// compares a role string or touches a non-existent is_client column.
// Split out of GrantClientAccessModal.tsx (module-size).
// =========================================================================

import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@wtc/ui";
import { useFormManager } from "@/lib/hooks/forms/use-form-manager";
import { useContacts, useClientSearch } from "@/lib/hooks/use-api";
import {
  useGrantClientAccess,
  useRevokeClientAccess,
  useClientUserAccess,
  useCompanyClientAccess,
  useRevokeCompanyClientAccess,
} from "@/lib/hooks/api/admin/sections/client-access";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import { describeClientAccessScope } from "@/lib/auth/access-levels";
import type { ContactUserAccess } from "@/lib/types/database";
import type { User } from "@/lib/types/user";
import { grantContactAccessSchema, type GrantContactAccessForm } from "./schema";

interface Params {
  isOpen: boolean;
  userId?: string;
  user?: User;
  access?: ContactUserAccess | null;
  onSuccess?: () => void;
  onClose: () => void;
}

export function useGrantContactAccessForm({
  isOpen,
  userId,
  user,
  access,
  onSuccess,
  onClose,
}: Params) {
  // Fallback user when neither `userId` nor `user` is supplied: the admin picks
  // one via UserSelect (a real, visible input — not a phantom required field).
  const [pickedUserId, setPickedUserId] = useState("");

  // Authoritative id, resolved synchronously every render (no reset timing):
  // explicit prop → display user's id → in-form pick.
  const effectiveUserId: string = userId ?? user?.id ?? pickedUserId;
  const hasPreselectedUser = Boolean(userId ?? user?.id);

  const form = useForm<GrantContactAccessForm>({
    resolver: zodResolver(grantContactAccessSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      contact_id: "",
      access_level: "read_only",
    },
  });

  // Reset the user-entered fields when the modal opens / switches to edit mode.
  // Intentionally keyed ONLY on `access` + `isOpen` — NOT on the user prop. A
  // late-arriving user/userId prop must not re-fire this reset and wipe the
  // contact the admin already picked (that clobber was the very class of bug
  // this redesign removes).
  useEffect(() => {
    if (!isOpen) return;
    form.reset({
      contact_id: access?.contact_id ?? "",
      access_level: access?.access_level ?? "read_only",
    });
  }, [access, isOpen, form]);

  // Clear the in-form user pick when the modal closes, so a reopened picker
  // starts empty. Separate from the field reset so it never disturbs edits.
  useEffect(() => {
    if (!isOpen) setPickedUserId("");
  }, [isOpen]);

  const formManager = useFormManager<GrantContactAccessForm>({
    form,
    draftKey: `grant-contact-access-${effectiveUserId || "new"}`,
    isOpen,
    onSuccess: () => {
      form.reset();
      onSuccess?.();
      onClose();
    },
    successMessage: access
      ? "Contact access updated successfully!"
      : "Contact access granted successfully!",
  });

  const {
    searchValue: searchTerm,
    debouncedSearchValue: debouncedSearchTerm,
    setSearchValue: setSearchTerm,
  } = useDebouncedSearch({ initialValue: "", delay: 300 });

  const { data: initialClients, isLoading: initialClientsLoading } = useContacts({
    isActive: true,
    limit: 20,
  });
  const { data: searchResults, isFetching: isSearching } = useClientSearch(debouncedSearchTerm);

  const clientOptions = useMemo(
    () => (debouncedSearchTerm ? searchResults : initialClients),
    [debouncedSearchTerm, searchResults, initialClients],
  );

  const grantAccessMutation = useGrantClientAccess();
  const revokeAccessMutation = useRevokeClientAccess();
  const revokeCompanyMutation = useRevokeCompanyClientAccess();

  const { data: existingContactGrants, isLoading: existingContactLoading } =
    useClientUserAccess(effectiveUserId || null);
  const { data: existingCompanyGrants, isLoading: existingCompanyLoading } =
    useCompanyClientAccess(effectiveUserId || null);

  const selectedContactId = form.watch("contact_id");
  const selectedAccessLevel = form.watch("access_level");

  const selectedContact = useMemo(() => {
    if (!selectedContactId) return null;
    const fromList = (clientOptions ?? []).find(
      (c: { id: string }) => c.id === selectedContactId,
    );
    if (fromList) return fromList;
    const fromGrant = (existingContactGrants ?? []).find(
      (g) => g.contact_id === selectedContactId,
    )?.contact;
    return fromGrant
      ? {
          id: selectedContactId,
          name: fromGrant.name,
          company_name: fromGrant.company_name,
        }
      : null;
  }, [selectedContactId, clientOptions, existingContactGrants]);

  const grantPreviewText = useMemo(() => {
    if (!selectedContactId || !selectedAccessLevel) return null;
    return describeClientAccessScope(selectedAccessLevel, {
      contactName: selectedContact?.name,
      companyName: selectedContact?.company_name,
    });
  }, [selectedContactId, selectedAccessLevel, selectedContact]);

  const handleRevokeContact = (contactId: string) => {
    if (!effectiveUserId) return;
    if (confirm("Revoke this user's access to the contact?")) {
      revokeAccessMutation.mutate({ userId: effectiveUserId, contactId });
    }
  };

  const handleRevokeCompany = (companyId: string) => {
    if (!effectiveUserId) return;
    if (
      confirm(
        "Revoke this user's company-wide access? They will lose access to all contacts at that company.",
      )
    ) {
      revokeCompanyMutation.mutate({ userId: effectiveUserId, companyId });
    }
  };

  const submitGrant = async (values: GrantContactAccessForm) => {
    if (!effectiveUserId) {
      formManager.showError("Select a user to grant access to.");
      return;
    }
    try {
      await grantAccessMutation.mutateAsync({
        userId: effectiveUserId,
        contactId: values.contact_id,
        accessLevel: values.access_level,
      });
      formManager.showSuccess(
        access ? "Contact access updated successfully!" : "Contact access granted successfully!",
      );
      form.reset();
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : access
            ? "Failed to update access"
            : "Failed to grant access";
      formManager.showError(message);
    }
  };

  return {
    form,
    formManager,
    effectiveUserId,
    hasPreselectedUser,
    pickedUserId,
    setPickedUserId,
    searchTerm,
    setSearchTerm,
    isSearching,
    initialClientsLoading,
    clientOptions,
    grantAccessMutation,
    revokeAccessMutation,
    revokeCompanyMutation,
    existingContactGrants,
    existingCompanyGrants,
    existingLoading: existingContactLoading || existingCompanyLoading,
    selectedAccessLevel,
    grantPreviewText,
    handleRevokeContact,
    handleRevokeCompany,
    submitGrant,
  };
}
